// The save. Versioned JSON in localStorage, with a tiny pub/sub so screens update.

import { defaultAvatar, STARTER_ITEMS, type AvatarData } from '@avatar/model';
import { freshSkill, type SkillState } from './skills';
import type { SkillId } from './types';

export interface Settings {
  tts: boolean;
  sound: boolean;
  sign: string;            // letters on the house
  softFloor: boolean;      // reserved: gentler ladder descent
}

import { migrateHouse, freshHouse, type HouseState } from './house';
export type { PlacedItem } from './house';

export type PetType = 'stone' | 'leaf' | 'spark' | 'wave' | 'frost' | 'star';
export interface Egg { id: string; type: PetType; earnedAt: number; roundsNeeded: number; roundsDone: number; from: string }
export interface Pet { id: string; type: PetType; name: string; stage: 1 | 2 | 3; fed: number; bornAt: number }

export interface Save {
  v: 1;
  created: number;
  avatar: AvatarData;
  gems: number;
  /** best stars per rung per game: stars[gameId][rung] = 0..3 */
  stars: Record<string, Record<string, number>>;
  charms: string[];
  owned: string[];               // item ids, decor ids, fun game ids
  house: HouseState;
  skills: Partial<Record<SkillId, SkillState>>;
  pins: Partial<Record<SkillId, [number, number]>>;
  settings: Settings;
  totals: { rounds: number; attempts: number; correct: number; gemsEarned: number; gemsSpent: number };
  lastPlayed: Record<string, number>;
  onboarded: boolean;
  town: { done: Record<string, string[]>; completed: Record<string, number>; gifts: string[] };
  pets: { eggs: Egg[]; pets: Pet[]; active?: string; milestones: number[] };
  /** things she made in games, shown in the house: kept[kind] = [{ t, data }] */
  kept: Record<string, { t: number; data: unknown }[]>;
  /** regions whose opening she has already been shown */
  regionsSeen: string[];
}

const KEY = 'edugames.save.v1';

export function freshSave(): Save {
  return {
    v: 1,
    created: Date.now(),
    avatar: defaultAvatar(),
    gems: 0,
    stars: {},
    charms: [],
    owned: [...STARTER_ITEMS],
    house: freshHouse(),
    skills: {},
    pins: {},
    settings: { tts: true, sound: true, sign: 'AR', softFloor: false },
    totals: { rounds: 0, attempts: 0, correct: 0, gemsEarned: 0, gemsSpent: 0 },
    lastPlayed: {},
    onboarded: false,
    town: { done: {}, completed: {}, gifts: [] },
    pets: { eggs: [], pets: [], milestones: [] },
    kept: {},
    regionsSeen: [],
  };
}

function load(): Save {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return freshSave();
    const s = JSON.parse(raw) as Save;
    return migrate(s);
  } catch { return freshSave(); }
}

function migrate(s: Save): Save {
  const f = freshSave();
  // shallow fill for any field added later
  return {
    ...f, ...s,
    settings: { ...f.settings, ...(s.settings || {}) },
    totals: { ...f.totals, ...(s.totals || {}) },
    house: migrateHouse(s.house),
    town: { ...f.town, ...(s.town || {}) },
    pets: { ...f.pets, ...(s.pets || {}) },
    kept: { ...(s.kept || {}) },
    regionsSeen: s.regionsSeen || [],
  };
}

type Listener = (s: Save) => void;
const listeners = new Set<Listener>();
let save: Save = load();
let timer: number | null = null;

export function getSave(): Save { return save; }

/** mutate inside fn; persists (debounced) and notifies */
export function update(fn: (s: Save) => void) {
  fn(save);
  persist();
  for (const l of listeners) l(save);
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

function persist() {
  if (timer) clearTimeout(timer);
  timer = window.setTimeout(() => {
    timer = null;
    try { localStorage.setItem(KEY, JSON.stringify(save)); } catch { /* storage full or blocked; keep playing */ }
  }, 150);
}

export function flushSave() {
  if (timer) { clearTimeout(timer); timer = null; }
  try { localStorage.setItem(KEY, JSON.stringify(save)); } catch { /* ignore */ }
}

export function replaceSave(s: Save) {
  save = migrate(s);
  flushSave();
  for (const l of listeners) l(save);
}

export function resetSave() {
  save = freshSave();
  flushSave();
  for (const l of listeners) l(save);
}

// ---- helpers ----

export function skill(id: SkillId): SkillState {
  if (!save.skills[id]) save.skills[id] = freshSkill();
  return save.skills[id]!;
}

export function totalStars(s: Save = save): number {
  let n = 0;
  for (const g of Object.values(s.stars)) for (const v of Object.values(g)) n += v;
  return n;
}

export function gameStars(gameId: string, s: Save = save): number {
  const g = s.stars[gameId];
  if (!g) return 0;
  return Object.values(g).reduce((a, b) => a + b, 0);
}

export function owns(id: string, s: Save = save): boolean {
  return s.owned.includes(id);
}

/** local calendar day key, so jobs roll over at her midnight */
export function dayKey(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

window.addEventListener('pagehide', flushSave);
document.addEventListener('visibilitychange', () => { if (document.hidden) flushSave(); });
